/**
 * Authenticated app shell: <Sidebar> + header (title/subtitle + notification bell + language + dark-mode toggle)
 * + scrollable main. User menu lives in the sidebar footer. Sidebar 240px⇄64px (persisted).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useLang, useT } from '../i18n/language-provider'
import { Sidebar } from './sidebar'
import { PushEnablePrompt } from './push-enable-prompt'
import { NotificationBell } from './notification-bell'
import { Icon } from './ui/icon'
import { PageTitleBlock } from './page-title-block'
import { redirectFor } from './nav-access'
import { getTheme, setTheme, type Theme } from '../lib/theme'
import { PageActionsContext } from '../lib/page-actions'
import { getAccess } from '../lib/api-client'
import { authClient } from '../lib/auth-client'
import { AccessContext, DEFAULT_ACCESS } from '../lib/access-context'
import { EmployeeHandbookGate } from '../routes/employee-handbook-gate'

const COLLAPSE_KEY = 'sidebarCollapsed'

/**
 * Global header portal slot (same pattern as ui/tabs.tsx TabBarSlot). A page renders its shared
 * controls (e.g. the Marketing filter bar) inside <HeaderSlot> and they portal into the app
 * header, left of the language/theme toggles. When no page uses it the slot stays empty.
 */
/** Exported for tests that render a page outside AppLayout and still need its header controls to mount. */
export const HeaderSlotContext = createContext<HTMLDivElement | null>(null)

export function HeaderSlot({ children }: { children: ReactNode }) {
  const el = useContext(HeaderSlotContext)
  if (!el) return null
  return createPortal(children, el)
}

export function AppLayout() {
  const t = useT()
  const { locale, setLocale } = useLang()
  // localStorage can throw (private mode / quota) and there is no root ErrorBoundary to catch it.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1' } catch { return false }
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [theme, setThemeState] = useState<Theme>(getTheme())
  const [pageActions, setPageActions] = useState<ReactNode>(null)
  const [headerSlotEl, setHeaderSlotEl] = useState<HTMLDivElement | null>(null)

  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()

  // RBAC: restricted Developer-only viewers may only reach /developer routes.
  const { data: access } = useQuery({ queryKey: ['access'], queryFn: getAccess, staleTime: Infinity })
  const acc = (access?.success ? access.data : null) ?? DEFAULT_ACCESS
  // TEMPORARY login lock: backend returns 403 LOGIN_LOCKED for non-allowlisted accounts.
  const locked = !!access && !access.success && access.error.code === '403'
  // Session invalid/expired: /api/access returned 401 (cookie not sent / stale). Show an
  // explicit re-login screen instead of silently degrading to DEFAULT_ACCESS (no-role look)
  // — and instead of auto-navigating (which could stick on a blank state). The button signs
  // out (clears the session cookie) then routes to /login for a fresh OAuth flow.
  const sessionExpired = !!access && !access.success && access.error.code === '401'
  // Restricted scopes hide the sidebar entirely (external report-only + monetization-only).
  const hideSidebar = acc.reportOnly || acc.monetOnly
  useEffect(() => {
    // Wait for the access payload before any RBAC redirect. On a hard load the query is
    // still pending (acc falls back to DEFAULT_ACCESS, which has no role), so running the
    // redirect early would bounce deep-linked pages like /hr/leave to '/'. Skipping while
    // access is loading / locked / expired keeps hard refresh on the requested route.
    if (!access?.success) return
    const target = redirectFor(pathname, acc)
    if (target) void navigate({ to: target })
  }, [access, acc, pathname, navigate])

  function toggleCollapse() {
    setCollapsed((c) => {
      const next = !c
      // Swallow: a blocked write (private mode / quota) must not break sidebar toggling.
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0') } catch { /* ignore */ }
      return next
    })
  }

  function toggleTheme() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    setThemeState(next)
  }

  // Wait for the access profile before painting the shell — otherwise report-only viewers
  // briefly flash the full sidebar (rendered with DEFAULT_ACCESS) before it resolves + hides.
  if (access === undefined) {
    return <div className="flex h-screen items-center justify-center bg-app text-sm text-muted">{t('common.loading')}</div>
  }

  if (sessionExpired) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-app px-6 text-center">
        <Icon name="key" className="h-10 w-10 text-muted" />
        <div>
          <h1 className="text-lg font-bold text-fg">Phiên đăng nhập đã hết hạn</h1>
          <p className="mt-1 max-w-md text-sm text-muted">
            Vui lòng đăng nhập lại.
            <br />Your session has expired. Please sign in again.
          </p>
        </div>
        <button
          onClick={() => { void authClient.signOut().finally(() => { window.location.href = '/login' }) }}
          className="rounded-md border border-primary bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20"
        >
          {t('login.signInWithGoogle')}
        </button>
      </div>
    )
  }

  if (locked) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-app px-6 text-center">
        <Icon name="lock" className="h-10 w-10 text-muted" />
        <div>
          <h1 className="text-lg font-bold text-fg">Hệ thống đang tạm khoá</h1>
          <p className="mt-1 max-w-md text-sm text-muted">
            Tài khoản của bạn chưa được cấp quyền truy cập. Vui lòng liên hệ quản trị viên.
            <br />Your account is not authorized. Please contact the administrator.
          </p>
        </div>
        <button
          onClick={() => { void authClient.signOut().then(() => { window.location.href = '/login' }) }}
          className="rounded-md border border-border bg-surface px-4 py-2 text-sm text-fg-soft hover:bg-hover"
        >
          {t('common.signOut')}
        </button>
      </div>
    )
  }

  return (
    <AccessContext.Provider value={acc}>
    <div id="app-layout" className="flex h-screen overflow-hidden bg-app">
      {/* Desktop sidebar — hidden entirely for external report-only viewers */}
      {!hideSidebar && (
      <aside className={['hidden flex-shrink-0 border-r border-border md:block', collapsed ? 'w-16' : 'w-60'].join(' ')}>
        <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </aside>
      )}

      {/* Mobile drawer + backdrop */}
      {!hideSidebar && mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-60 border-r border-border shadow-xl">
            <Sidebar collapsed={false} onToggleCollapse={() => setMobileOpen(false)} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Right column: header + content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header id="app-header" className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-surface px-4 py-3">
          {!hideSidebar && <button onClick={() => setMobileOpen(true)} className="inline-flex items-center justify-center rounded p-1 text-muted hover:bg-hover md:hidden" aria-label={t('layout.toggleSidebar')}><Icon name="menu" className="h-5 w-5" /></button>}

          {/* Title + subtitle */}
          <PageTitleBlock pathname={pathname} />

          {/* Page actions (registered by the active page) */}
          {pageActions && <div className="flex shrink-0 items-center gap-2">{pageActions}</div>}

          {/* Header slot — shared page controls portaled here (e.g. Marketing filter bar).
              On mobile it drops to its own full-width row below the title/toggles; inline on sm+. */}
          <div ref={setHeaderSlotEl} className="order-last flex w-full flex-wrap items-center justify-start gap-2 empty:hidden sm:order-none sm:w-auto sm:justify-end" />

          {/* In-app notifications (every web-push fan-out lands here too). External report-only /
              monetization-only viewers are never addressed by a push, so they get no bell. */}
          {!hideSidebar && <NotificationBell enabled={!!access?.success} />}

          {/* Language toggle — shows the target language */}
          <button
            id="layout-lang-toggle-btn"
            onClick={() => setLocale(locale === 'vi' ? 'en' : 'vi')}
            className="rounded px-2 py-0.5 text-xs font-medium text-muted hover:bg-hover hover:text-fg-soft"
            title={locale === 'vi' ? 'English' : 'Tiếng Việt'}
          >
            {locale === 'vi' ? 'EN' : 'VI'}
          </button>

          {/* Dark-mode toggle */}
          <button id="layout-theme-toggle-btn" onClick={toggleTheme} title={t('layout.toggleTheme')} aria-label={t('layout.toggleTheme')} className="rounded p-1.5 text-muted hover:bg-hover hover:text-fg-soft">
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} className="h-5 w-5" />
          </button>
        </header>

        <main id="app-main" className="flex-1 overflow-y-auto">
          <div className="w-full px-4 py-6 md:px-6">
            <PageActionsContext.Provider value={setPageActions}>
              <HeaderSlotContext.Provider value={headerSlotEl}>
                {/* Sổ tay + bài kiểm tra là bước bắt buộc: chưa xong thì mọi trang khác về /handbook. */}
                <EmployeeHandbookGate>
                  <Outlet />
                </EmployeeHandbookGate>
              </HeaderSlotContext.Provider>
            </PageActionsContext.Provider>
          </div>
        </main>
      </div>
      <PushEnablePrompt />
    </div>
    </AccessContext.Provider>
  )
}
